import { Box, Button, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { ReminderEntityTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ReminderFormFields } from "web_myplann/Reminders/Components/ReminderFormFields";
import { getReminderEntityConfig } from "web_myplann/Reminders/Config/reminderEntityConfig";
import type { ReminderFormModel } from "web_myplann/Reminders/Forms/reminderFormSchema";
import { useReminderForm } from "web_myplann/Reminders/Hooks/useReminderForm";
import { getEditableTemplates } from "web_myplann/Reminders/Utils/getEditableTemplates";
import {
	IDENTIFICATION_DOCUMENT_DRAFT_TEMPLATES,
	type ReminderDraft,
} from "web_myplann/Reminders/Utils/reminderDrafts";
import { SmallDrawer } from "web_myplann/Shared/Components/SmallDrawer";

const ENTITY_CONFIG = getReminderEntityConfig(
	ReminderEntityTypeEnum.IdentificationDocument,
);

type Props = {
	validTo: Date;
	draft?: ReminderDraft;
	onSave: (model: ReminderFormModel) => void;
	onClose: () => void;
};

export const ReminderDraftDrawer = ({
	validTo,
	draft,
	onSave,
	onClose,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const templates = useMemo(() => {
		return getEditableTemplates(
			IDENTIFICATION_DOCUMENT_DRAFT_TEMPLATES,
			ENTITY_CONFIG,
			validTo,
			draft?.model.typeKey,
		);
	}, [validTo, draft?.model.typeKey]);

	const { form } = useReminderForm({
		entityConfig: ENTITY_CONFIG,
		templates,
		validTo,
		initialModel: draft?.model,
	});

	const hasNothingToAdd = templates.length === 0;

	const submit = form.handleSubmit((model) => {
		onSave(model);
		onClose();
	});

	return (
		<SmallDrawer
			open
			onClose={onClose}
			title={t(
				draft
					? Resources.Reminders.Form.editTitle
					: Resources.Reminders.Form.addTitle,
			)}
		>
			{hasNothingToAdd ? (
				<Typography
					variant="body2"
					px={theme.spacingTokens.lg}
					py={theme.spacingTokens.lg}
				>
					{t(Resources.Reminders.Form.noTemplatesHint)}
				</Typography>
			) : (
				<ReminderFormFields
					form={form}
					templates={templates}
					entityConfig={ENTITY_CONFIG}
					validTo={validTo}
				/>
			)}

			<Box px={theme.spacingTokens.lg} py={theme.spacingTokens.lg}>
				<Button
					variant="primary"
					onClick={submit}
					disabled={!form.formState.isValid || hasNothingToAdd}
				>
					{t(
						draft
							? Resources.Reminders.Form.editCta
							: Resources.Reminders.Form.addCta,
					)}
				</Button>
			</Box>
		</SmallDrawer>
	);
};
